import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Paper, Typography, TextField, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import PrintRounded from '@mui/icons-material/PrintRounded';
import { useAuth } from '../../state/AuthContext';
import { fetchStats } from '../../data/apiClient';
import { PRINT_SERVER } from '../../data/printServer';
import type { StatsSummary } from '../../shared/statsAggregate';
import { formatPrice } from '../../utils/format';

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function startOfDayMs(dateInputValue: string): number {
  return new Date(`${dateInputValue}T00:00:00`).getTime();
}

function endOfDayMs(dateInputValue: string): number {
  return new Date(`${dateInputValue}T23:59:59.999`).getTime();
}

const ALL_TIME_FROM = 0;

const StatsScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const today = toDateInputValue(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [summary, setSummary] = useState<StatsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printSnackbar, setPrintSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const load = useCallback((fromMs: number, toMs: number) => {
    setLoading(true);
    setError(null);
    fetchStats(fromMs, toMs)
      .then(setSummary)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(startOfDayMs(from), endOfDayMs(to));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAdmin) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, textAlign: 'center', color: '#9E9E9E' }}>
        <Typography>{t('admin.accessDenied')}</Typography>
      </Container>
    );
  }

  const handleApplyRange = (): void => {
    load(startOfDayMs(from), endOfDayMs(to));
  };

  const handlePrintSummary = async (): Promise<void> => {
    if (!summary) return;
    setPrinting(true);
    try {
      const r = await fetch(`${PRINT_SERVER}/print/stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromLabel: from, toLabel: to, summary }),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Print failed');
      setPrintSnackbar({ open: true, message: t('stats.summaryPrinted'), severity: 'success' });
    } catch (e: any) {
      setPrintSnackbar({ open: true, message: t('stats.printFailed', { message: e.message }), severity: 'error' });
    } finally {
      setPrinting(false);
    }
  };

  const handleQuickSelect = (range: 'today' | 'week' | 'all'): void => {
    const now = new Date();
    if (range === 'today') {
      const value = toDateInputValue(now);
      setFrom(value);
      setTo(value);
      load(startOfDayMs(value), endOfDayMs(value));
    } else if (range === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 6);
      const fromValue = toDateInputValue(weekAgo);
      const toValue = toDateInputValue(now);
      setFrom(fromValue);
      setTo(toValue);
      load(startOfDayMs(fromValue), endOfDayMs(toValue));
    } else {
      const toValue = toDateInputValue(now);
      setFrom(toDateInputValue(new Date(ALL_TIME_FROM)));
      setTo(toValue);
      load(ALL_TIME_FROM, endOfDayMs(toValue));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'primary.main', textTransform: 'uppercase', letterSpacing: '0.1em', px: 0.5, mb: 1 }}>
        {t('stats.title')}
      </Typography>

      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #F5F5F5' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.25 }}>
          <TextField
            label={t('stats.from')}
            type="date"
            size="small"
            fullWidth
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('stats.to')}
            type="date"
            size="small"
            fullWidth
            value={to}
            onChange={(e) => setTo(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={() => handleQuickSelect('today')}>
            {t('stats.today')}
          </Button>
          <Button size="small" variant="outlined" onClick={() => handleQuickSelect('week')}>
            {t('stats.thisWeek')}
          </Button>
          <Button size="small" variant="outlined" onClick={() => handleQuickSelect('all')}>
            {t('stats.allTime')}
          </Button>
          <Button size="small" variant="contained" onClick={handleApplyRange} sx={{ ml: 'auto' }}>
            {t('stats.apply')}
          </Button>
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Typography sx={{ color: 'error.main', textAlign: 'center', py: 2 }}>
          {t('stats.loadFailed', { message: error })}
        </Typography>
      )}

      {!loading && !error && summary && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1.25 }}>
            <Paper elevation={0} sx={{ backgroundColor: 'primary.main', p: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('stats.turnover')}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#fff' }}>
                {formatPrice(summary.turnover, i18n.language)}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: '1px solid #F5F5F5' }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#9E9E9E', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('stats.itemsSold')}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#212121' }}>
                {summary.itemsSold}
              </Typography>
            </Paper>
          </Box>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: '1px solid #F5F5F5', display: 'flex', justifyContent: 'space-around' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#9E9E9E' }}>{t('bill.cash')}</Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatPrice(summary.byMethod.cash, i18n.language)}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#9E9E9E' }}>{t('bill.card')}</Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatPrice(summary.byMethod.card, i18n.language)}</Typography>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #F5F5F5' }}>
            <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: '1px solid #F5F5F5' }}>
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', color: '#9E9E9E', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                {t('stats.byItem')}
              </Typography>
            </Box>
            {summary.byItem.length === 0 && (
              <Typography sx={{ px: 2, py: 2, color: '#9E9E9E' }}>{t('stats.noSales')}</Typography>
            )}
            {summary.byItem.map((item) => (
              <Box
                key={item.menuItemId}
                sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F9F9F9' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{ backgroundColor: '#FFECEC', color: 'primary.main', borderRadius: '8px', fontWeight: 700, fontSize: '0.75rem', px: 1.125, py: 0.25 }}>
                    {item.quantity}×
                  </Box>
                  <Typography sx={{ fontSize: '0.9rem', color: '#424242' }}>{item.name}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#212121' }}>
                  {formatPrice(item.revenue, i18n.language)}
                </Typography>
              </Box>
            ))}
          </Paper>

          <Button
            fullWidth
            variant="outlined"
            onClick={handlePrintSummary}
            disabled={printing}
            startIcon={<PrintRounded />}
            sx={{ mt: 1.25, borderColor: '#E0E0E0', color: '#757575' }}
          >
            {printing ? t('stats.summaryPrinting') : t('stats.printSummary')}
          </Button>
        </>
      )}

      <Snackbar
        open={printSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPrintSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={printSnackbar.severity} onClose={() => setPrintSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {printSnackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StatsScreen;
